import { createTheme } from '@mui/material/styles';

const SIDEBAR_WIDTH = 240;

const theme = createTheme({
    palette: {
        primary: {
            main: '#2563eb',
        },
        secondary: {
            main: '#181c23',
        },
        background: {
            default: '#f9fafb',
            paper: '#fff',
        },
        text: {
            primary: '#181c23',
            secondary: '#6b7280',
        },
        sidebar: {
            bg: '#181c23',
            text: '#b0b3b8',
            selectedText: '#fff',
            selectedGradient: 'linear-gradient(90deg, #2563eb 0%, #233a63 100%)',
            sectionHeader: '#8a8f98',
            divider: '#23272f',
            highlight: '#2563eb',
        },
        header: {
            bg: '#fff',
        },
    },
    shape: {
        borderRadius: 4, // Subtle corners; avoids overly rounded look on short cards and chips
    },
    sidebar: {
        width: SIDEBAR_WIDTH,
    },
    typography: {
        fontFamily: 'Cabin, sans-serif',
        fontSize: 18,
        fontWeightRegular: 400,
        h1: {
            fontSize: '2.5rem',
            fontWeight: 500,
            fontFamily: 'Cabin, sans-serif',
        },
        h2: {
            fontSize: '2rem',
            fontWeight: 500,
            fontFamily: 'Cabin, sans-serif',
        },
        h3: {
            fontSize: '1.75rem',
            fontWeight: 500,
            fontFamily: 'Cabin, sans-serif',
        },
        h4: {
            fontSize: '1.5rem',
            fontWeight: 700,
            fontFamily: 'Cabin, sans-serif',
        },
        h5: {
            fontSize: '1.25rem',
            fontWeight: 500,
            fontFamily: 'Cabin, sans-serif',
        },
        h6: {
            fontSize: '1rem',
            fontWeight: 600,
            fontFamily: 'Cabin, sans-serif',
        },
        body1: {
            fontSize: '1.125rem', // 18px
            fontWeight: 400,
            color: '#222',
            fontFamily: 'Cabin, sans-serif',
        },
        body2: {
            fontSize: '1rem',
            fontWeight: 400,
            fontFamily: 'Cabin, sans-serif',
        },
        subtitle1: {
            fontSize: '1rem',
            fontWeight: 500,
            fontFamily: 'Cabin, sans-serif',
        },
        subtitle2: {
            fontSize: '0.875rem',
            fontWeight: 500,
            fontFamily: 'Cabin, sans-serif',
        },
        caption: {
            fontSize: '0.75rem',
            fontWeight: 400,
            fontFamily: 'Cabin, sans-serif',
            color: '#6b7280',
        },
        button: {
            fontSize: '1rem', // 16px — matches form/dropdown label size
            fontWeight: 600,
            fontFamily: 'Cabin, sans-serif',
            textTransform: 'none',
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    textTransform: 'none',
                    fontWeight: 600,
                },
                outlinedPrimary: {
                    borderColor: '#2563eb',
                    color: '#2563eb',
                    background: '#fff',
                    '&:hover': {
                        borderColor: '#2563eb',
                        background: 'rgba(37,99,235,0.08)',
                    },
                },
                containedPrimary: {
                    backgroundColor: '#2563eb',
                    color: '#fff',
                    boxShadow: '0 2px 8px rgba(37,99,235,0.20)',
                    '&:hover': {
                        backgroundColor: '#1d4ed8',
                        boxShadow: '0 4px 12px rgba(37,99,235,0.30)',
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: 4,
                    boxShadow: '0 18px 45px rgba(15,23,42,0.12)', // stronger elevation so cards stand out
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 4,
                    boxShadow: '0 4px 12px rgba(15,23,42,0.08)',
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    backgroundColor: '#2563eb',
                    height: 4,
                    borderRadius: 2,
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    fontWeight: 400,
                    fontSize: '1.125rem', // 18px
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    '& .MuiTableCell-head': {
                        fontSize: '14px',
                        fontWeight: 600,
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 4,
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                asterisk: {
                    color: '#f97316', // Orange color for required field asterisk
                    fontWeight: 700,
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                asterisk: {
                    color: '#f97316', // Orange color for required field asterisk
                    fontWeight: 700,
                },
            },
        },
        // Form input consistent sizing
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    fontSize: '1rem', // 16px - consistent form input size
                },
                input: {
                    fontSize: '1rem',
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    fontSize: '1rem',
                },
                input: {
                    fontSize: '1rem',
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    fontSize: '1rem',
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                input: {
                    fontSize: '1rem',
                },
                option: {
                    fontSize: '1rem',
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontSize: '1rem',
                },
            },
        },
    },
});

export default theme; 